import React, { useEffect, useState } from 'react';
import { errorToast, successToast, getLocalStorageItem } from '../../utils/helper';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import { CompanyApi } from '../../api/companyApi';
import ToggleSwitch from '../../component/common/ToggleSwitch';

const CompanyAddUsersModal = ({ isOpen, onClose, onSuccess }) => {
  const [loader, setLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]); // Users to be added (havePlan: false -> true)
  const [removedUsers, setRemovedUsers] = useState([]); // Users to be removed (havePlan: true -> false)
  const [searchTerm, setSearchTerm] = useState('');
  const [schemeData, setSchemeData] = useState(null);

  const userData = getLocalStorageItem('userData')
    ? JSON.parse(getLocalStorageItem('userData'))
    : null;

  useEffect(() => {
    if (isOpen) {
      fetchCompanyScheme();
    }
  }, [isOpen]);

  useEffect(() => {
    if (schemeData) {
      fetchUnenrolledUsers();
    }
  }, [schemeData]);

  // Debounce user search
  useEffect(() => {
    if (searchTerm !== undefined && schemeData) {
      const delayDebounceFn = setTimeout(() => {
        fetchUnenrolledUsers(searchTerm);
      }, 800);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  const fetchCompanyScheme = async () => {
    setLoader(true);
    try {
      // Get company's health scheme
      const response = await CompanyApi.getHealthScheme(userData?.companyId);

      if (response?.data?.meta?.code === 1) {
        const scheme = response?.data?.data;
        if (scheme) {
          setSchemeData(scheme);
        } else {
          errorToast('No active health scheme found for your company');
          onClose();
        }
      } else {
        errorToast(response?.data?.message || 'Failed to fetch scheme');
        onClose();
      }
    } catch (error) {
      console.error('Error fetching scheme:', error);
      errorToast('Failed to fetch scheme');
      onClose();
    } finally {
      setLoader(false);
    }
  };

  const fetchUnenrolledUsers = async (searchTerm = '') => {
    if (!schemeData) return;

    setLoader(true);
    try {
      // TODO: Replace with actual health scheme users API when available
      const response = await CompanyApi.getHealthSchemeEligibleUsers(searchTerm);

      if (response?.data?.meta?.code === 1) {
        const usersList = response?.data?.data || [];

        // Transform users to include havePlan status

        setUsers(usersList);

        // Initialize selected users based on havePlan
        const initialSelected = usersList
          .filter((user) => user.havePlan)
          .map((user) => user.userId);
        setSelectedUsers(initialSelected);
      } else {
        errorToast(response?.data?.message || 'Failed to fetch users');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      errorToast('Failed to fetch users');
      setUsers([]);
    } finally {
      setLoader(false);
    }
  };

  const handleUserToggle = (user) => {
    console.log(user);
    const userId = user.userId || user._id;

    if (user.havePlan) {
      // User already has plan - toggling will remove them
      setRemovedUsers((prev) => {
        if (prev.includes(userId)) {
          // Already in removed list, toggle back (remove from removed list)
          return prev.filter((id) => id !== userId);
        } else {
          // Add to removed list
          return [...prev, userId];
        }
      });

      // Remove from added list if present
      setAddedUsers((prev) => prev.filter((id) => id !== userId));
    } else {
      // User doesn't have plan - toggling will add them
      setAddedUsers((prev) => {
        if (prev.includes(userId)) {
          // Already in added list, toggle back (remove from added list)
          return prev.filter((id) => id !== userId);
        } else {
          // Add to added list
          return [...prev, userId];
        }
      });

      // Remove from removed list if present
      setRemovedUsers((prev) => prev.filter((id) => id !== userId));
    }

    // Update selectedUsers for UI state
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      // Deselect all
      setSelectedUsers([]);
      setAddedUsers([]);
      setRemovedUsers([]);
    } else {
      // Select all
      const allUserIds = users.map((user) => user.userId || user._id);
      setSelectedUsers(allUserIds);

      // Set added users (those without plan)
      const usersToAdd = users.filter((user) => !user.havePlan).map((user) => user.userId);
      setAddedUsers(usersToAdd);

      // Clear removed users
      setRemovedUsers([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!schemeData) {
      errorToast('No active scheme found');
      return;
    }

    if (addedUsers.length === 0 && removedUsers.length === 0) {
      errorToast('Please select at least one user to add or remove');
      return;
    }

    setLoader(true);
    try {
      const reqBody = {
        schemeId: schemeData._id,
        userIds: addedUsers.map((userId) => userId.toString()),
        removeUserIds: removedUsers.map((userId) => userId.toString())
      };

      // TODO: Replace with actual health scheme enrollment API
      const response = await CompanyApi.enrollUsersInHealthScheme(reqBody);

      if (response?.data?.meta?.code === 1) {
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        errorToast(response?.data?.message || 'Failed to update users');
      }
    } catch (error) {
      console.error('Error updating users:', error);
      errorToast('Failed to update users');
    } finally {
      setLoader(false);
    }
  };

  const handleClose = () => {
    setUsers([]);
    setSelectedUsers([]);
    setAddedUsers([]);
    setRemovedUsers([]);
    setSearchTerm('');
    setSchemeData(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {loader && <Loader />}
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose}></div>

      {/* Modal */}
      <div className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">
            Manage Users in Health Scheme
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Scheme Info Display */}
            {schemeData && (
              <div className="bg-gradient-to-r from-shoorah-primary/10 to-shoorah-secondary/10 dark:from-shoorah-primary/20 dark:to-shoorah-secondary/20 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Active Health Scheme
                </h3>
                <p className="text-base font-semibold text-shoorah-primary dark:text-white">
                  {schemeData.providerName || 'Shoorah Health'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {schemeData.isCustomPlan ? 'Custom Plan' : 'Shoorah Plan'}
                </p>
              </div>
            )}

            {/* User Search */}
            {schemeData && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Users
                </label>
                <SearchInput
                  id="searchUsers"
                  name="searchUsers"
                  type="text"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                />
              </div>
            )}

            {/* Users List */}
            {schemeData && users.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Users <span className="text-red-500">*</span>
                    <span className="text-gray-500 font-normal ml-2">
                      ({selectedUsers.length} selected)
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-shoorah-primary hover:text-shoorah-secondary"
                  >
                    {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto">
                  {users.map((user) => {
                    const userId = user.userId || user._id;
                    // Determine the toggle state based on original havePlan and user actions
                    let isToggled = user.havePlan;

                    if (user.havePlan) {
                      // If user has plan, check if they're in removed list
                      isToggled = !removedUsers.includes(userId);
                    } else {
                      // If user doesn't have plan, check if they're in added list
                      isToggled = addedUsers.includes(userId);
                    }

                    return (
                      <div
                        key={userId}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-shoorah-darkBorder last:border-b-0"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                        </div>
                        <ToggleSwitch
                          toggleValue={isToggled}
                          setToggleValue={() => handleUserToggle(user)}
                          disabled={false}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Summary */}
                {(addedUsers.length > 0 || removedUsers.length > 0) && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {addedUsers.length > 0 && (
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {addedUsers.length} user(s) will be added
                        </span>
                      )}
                      {addedUsers.length > 0 && removedUsers.length > 0 && (
                        <span className="mx-2">•</span>
                      )}
                      {removedUsers.length > 0 && (
                        <span className="text-red-600 dark:text-red-400 font-medium">
                          {removedUsers.length} user(s) will be removed
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* No users message */}
            {schemeData && users.length === 0 && !loader && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No users found.</p>
                <p className="text-sm mt-1">Try adjusting your search criteria.</p>
              </div>
            )}

            {/* Loading scheme message */}
            {!schemeData && loader && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>Loading scheme information...</p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!schemeData || (addedUsers.length === 0 && removedUsers.length === 0)}
              className="btn-primary flex-1"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyAddUsersModal;
